<script>
  import { onMount, onDestroy } from 'svelte';
  import { accountHeroSection } from './account-hero.js';

  // Если у юзера в orders есть хоть один оплаченный — статус «билет активен»,
  // иначе «ожидает оплаты» (нейтральная подача, без алярма).
  export let hasPaidOrder = false;
  export let userName = '';

  const FORUM_TARGET_MS = new Date('2026-06-18T09:00:00+07:00').getTime();
  const pad = (n) => String(n).padStart(2, '0');

  let now = Date.now();
  let intervalId;

  onMount(() => {
    now = Date.now();
    intervalId = setInterval(() => {
      now = Date.now();
    }, 1000);
  });

  onDestroy(() => {
    if (intervalId) clearInterval(intervalId);
  });

  $: diff = Math.max(0, FORUM_TARGET_MS - now);
  $: days = pad(Math.floor(diff / 86_400_000));
  $: hours = pad(Math.floor((diff % 86_400_000) / 3_600_000));
  $: minutes = pad(Math.floor((diff % 3_600_000) / 60_000));
  $: seconds = pad(Math.floor((diff % 60_000) / 1000));
  $: started = diff === 0;
  $: headline = hasPaidOrder ? accountHeroSection.titleReady : accountHeroSection.titleWelcome;
  $: countdownAria = started
    ? 'Форум начался'
    : `${accountHeroSection.countdown.label} ${days}:${hours}:${minutes}:${seconds}`;
</script>

<section class="hero" aria-labelledby="hero-title">
  <div class="hero-divider" aria-hidden="true"></div>

  <div class="hero-inner">
    <div class="hero-copy">
      <p class="kicker">{accountHeroSection.kicker}</p>
      <h1 id="hero-title">{userName ? `Здравствуйте, ${userName}` : headline}</h1>
      <p class="subtitle">{accountHeroSection.subtitle}</p>
    </div>

    <div class="countdown-card" aria-label={countdownAria}>
      <div class="countdown-head">
        <span class="countdown-label">{accountHeroSection.countdown.label}</span>
        <span class="badge" class:badge-paid={hasPaidOrder} class:badge-pending={!hasPaidOrder}>
          <span class="badge-dot" aria-hidden="true"></span>
          {hasPaidOrder ? accountHeroSection.countdown.activeBadge : accountHeroSection.countdown.pendingBadge}
        </span>
      </div>

      <div class="countdown-grid">
        <div class="countdown-cell">
          <strong class="countdown-value">{days}</strong>
          <span class="countdown-unit">{accountHeroSection.countdown.units[0]}</span>
        </div>
        <div class="countdown-cell">
          <strong class="countdown-value">{hours}</strong>
          <span class="countdown-unit">{accountHeroSection.countdown.units[1]}</span>
        </div>
        <div class="countdown-cell">
          <strong class="countdown-value">{minutes}</strong>
          <span class="countdown-unit">{accountHeroSection.countdown.units[2]}</span>
        </div>
        <div class="countdown-cell">
          <strong class="countdown-value">{seconds}</strong>
          <span class="countdown-unit">{accountHeroSection.countdown.units[3]}</span>
        </div>
      </div>
    </div>
  </div>
</section>

<style>
  .hero {
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.025);
    padding: 40px;
    box-sizing: border-box;
  }

  /* Тонкая золотая «нить» справа — только на десктопе */
  .hero-divider {
    position: absolute;
    top: 0;
    right: 0;
    width: 1px;
    height: 100%;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(231, 185, 98, 0.3) 50%,
      transparent 100%
    );
    pointer-events: none;
  }

  .hero-inner {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    gap: 32px;
  }

  .hero-copy {
    max-width: 650px;
    min-width: 0;
    flex: 1 1 auto;
  }

  .kicker {
    margin: 0 0 12px;
    color: #e7b962;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.2;
    text-transform: uppercase;
    letter-spacing: 0.12em;
  }

  h1 {
    margin: 0;
    color: #ffffff;
    font-size: 32px;
    font-weight: 600;
    line-height: 1.15;
    letter-spacing: -0.015em;
  }

  .subtitle {
    margin: 20px 0 0;
    max-width: 560px;
    color: rgba(255, 255, 255, 0.55);
    font-size: 16px;
    line-height: 24px;
  }

  .countdown-card {
    flex: 0 0 auto;
    width: 100%;
    max-width: 300px;
    box-sizing: border-box;
    padding: 16px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
  }

  .countdown-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .countdown-label {
    color: rgba(255, 255, 255, 0.4);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.12em;
  }

  .badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 500;
  }

  .badge-paid {
    color: #7ed321;
  }

  .badge-pending {
    color: rgba(255, 255, 255, 0.5);
  }

  .badge-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }

  .countdown-grid {
    margin-top: 12px;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
    text-align: center;
  }

  .countdown-cell {
    padding: 12px 8px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.04);
  }

  .countdown-value {
    display: block;
    color: #ffffff;
    font-size: 20px;
    font-weight: 500;
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
  }

  .countdown-unit {
    display: block;
    margin-top: 2px;
    color: rgba(255, 255, 255, 0.35);
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.12em;
  }

  /* ============== Tablet (≤1279px) ============== */
  @media (max-width: 1279px) {
    .hero {
      padding: 32px;
    }

    .hero-divider {
      display: none;
    }

    .hero-inner {
      flex-direction: column;
      align-items: stretch;
      gap: 32px;
    }

    .hero-copy {
      max-width: 100%;
    }

    h1 {
      font-size: 28px;
    }

    .subtitle {
      font-size: 15px;
      line-height: 22px;
    }

    .countdown-card {
      max-width: 360px;
    }
  }

  /* ============== Mobile (≤767px) ============== */
  @media (max-width: 767px) {
    .hero {
      padding: 24px 20px;
    }

    h1 {
      font-size: 24px;
    }

    .subtitle {
      margin-top: 20px;
      font-size: 15px;
      line-height: 24px;
    }

    .countdown-card {
      max-width: 100%;
    }

    .countdown-head {
      gap: 12px;
    }

    .countdown-value {
      font-size: 18px;
    }
  }
</style>
